import { useState } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { apiClient } from "@/services/api";
import { formatHours } from "@/utils/ingestCardModel";
import {
  groupEventsByDay,
  isValidMonthParam,
  monthLabel,
  monthParamFor,
  shiftMonthParam,
  summaryFor,
  totalSummarySeconds,
} from "@/utils/footageUsage";
import FootageTimelineItem from "@/components/usage/FootageTimelineItem";

type TypeFilter = "all" | "uploaded" | "streamed";

const FILTERS: { value: TypeFilter; labelKey: string }[] = [
  { value: "all", labelKey: "filterAll" },
  { value: "uploaded", labelKey: "filterUploaded" },
  { value: "streamed", labelKey: "filterStreamed" },
];

export default function FootageUsagePage() {
  const { t, i18n } = useTranslation("footageUsage");
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  const currentMonth = monthParamFor(new Date());
  const rawMonth = searchParams.get("month");
  const month = isValidMonthParam(rawMonth) ? rawMonth : currentMonth;

  const setMonth = (next: string) => {
    setSearchParams(next === currentMonth ? {} : { month: next });
  };

  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isPlaceholderData,
  } = useInfiniteQuery({
    queryKey: ["usage-events", user?.id, month, typeFilter],
    queryFn: ({ pageParam }) =>
      apiClient.getUserUsageEvents(user!.id, {
        month,
        type: typeFilter,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    enabled: Boolean(user),
    retry: false,
    placeholderData: keepPreviousData,
  });

  // Allowance semantics only exist for the current month.
  const { data: quota } = useQuery({
    queryKey: ["quota", user?.id],
    queryFn: () => apiClient.getUserQuota(user!.id),
    enabled: Boolean(user) && month === currentMonth,
    staleTime: 5 * 60 * 1000,
  });

  const summary = data?.pages[0]?.summary;
  const events = data?.pages.flatMap((pageData) => pageData.events) ?? [];
  const dayGroups = groupEventsByDay(events);

  const totalSeconds = data != null ? totalSummarySeconds(summary) : null;
  const uploaded = summaryFor(summary, "uploaded");
  const streamed = summaryFor(summary, "streamed");
  const allowance = quota?.planLimits.monthlyIngestSeconds;
  const remainingSeconds =
    allowance != null && totalSeconds != null
      ? Math.max(0, allowance - totalSeconds)
      : null;
  const usedPct =
    allowance != null && allowance > 0 && totalSeconds != null
      ? Math.floor((totalSeconds / allowance) * 100)
      : null;
  const resetLabel = quota
    ? new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(new Date(quota.period.end))
    : "";

  const dayFormatter = new Intl.DateTimeFormat(i18n.language, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const timeFormatter = new Intl.DateTimeFormat(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });

  // Return to the referring page (the card is mounted on both the dashboard
  // and MyPage and passes its location in link state); fall back to the
  // dashboard on a direct load.
  const backTo =
    (location.state as { from?: string } | null)?.from ?? "/dashboard";

  return (
    <div className="page-container py-8">
      <div className="mb-6">
        <Link
          to={backTo}
          className="text-sm text-secondary-500 hover:text-secondary-700"
        >
          {t("back")}
        </Link>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <h1 className="page-title">{t("title")}</h1>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t("prevMonth")}
              onClick={() => setMonth(shiftMonthParam(month, -1))}
              className="btn-secondary !min-h-0 rounded-md px-2.5 py-1.5"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-sm font-semibold tabular-nums text-secondary-900">
              {monthLabel(month, i18n.language)}
            </span>
            <button
              type="button"
              aria-label={t("nextMonth")}
              onClick={() => setMonth(shiftMonthParam(month, 1))}
              disabled={month === currentMonth}
              className="btn-secondary !min-h-0 rounded-md px-2.5 py-1.5"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p className="page-subtitle">{t("subtitle")}</p>
      </div>

      <div
        className={`mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4 ${
          isPlaceholderData ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-secondary-500">
            {t("statUploaded")}
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-secondary-900">
            {data != null ? formatHours(uploaded.seconds) : "—"}
          </div>
          <div className="mt-1 text-xs text-secondary-500">
            {data != null ? uploaded.count : "—"} ·{" "}
            {t("filterUploaded").toLowerCase()}
          </div>
        </div>
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-secondary-500">
            {t("statStreamed")}
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-secondary-900">
            {data != null ? formatHours(streamed.seconds) : "—"}
          </div>
          <div className="mt-1 text-xs text-secondary-500">
            {data != null ? streamed.count : "—"} ·{" "}
            {t("filterStreamed").toLowerCase()}
          </div>
        </div>
        <div className="rounded-lg border border-secondary-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-secondary-500">
            {t("statTotal")}
          </div>
          <div className="mt-1 text-2xl font-bold tabular-nums text-secondary-900">
            {totalSeconds != null ? formatHours(totalSeconds) : "—"}
          </div>
          {month === currentMonth && (
            <div className="mt-1 text-xs text-secondary-500">
              {t("statOfAllowance", {
                total: allowance != null ? formatHours(allowance) : "—",
              })}
            </div>
          )}
        </div>
        {month === currentMonth && (
          <div className="rounded-lg border border-secondary-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wider text-secondary-500">
              {t("statRemaining")}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-secondary-900">
              {remainingSeconds != null ? formatHours(remainingSeconds) : "—"}
            </div>
            <div className="mt-1 text-xs text-secondary-500">
              {usedPct != null
                ? t("statRemainingSub", { pct: usedPct, date: resetLabel })
                : " "}
            </div>
          </div>
        )}
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex items-center rounded-lg border border-secondary-200 bg-white p-1 text-sm"
          role="group"
          aria-label={t("filterAriaLabel")}
        >
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              aria-pressed={typeFilter === filter.value}
              onClick={() => setTypeFilter(filter.value)}
              className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                typeFilter === filter.value
                  ? "bg-secondary-900 text-white"
                  : "text-secondary-600 hover:bg-secondary-100"
              }`}
            >
              {t(filter.labelKey)}
            </button>
          ))}
        </div>
        <span className="text-xs text-secondary-500">{t("timesUtc")}</span>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
          <span className="ml-3 text-secondary-600">{t("loading")}</span>
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="font-medium text-red-800">{t("errorTitle")}</div>
          <div className="mt-1 text-sm text-red-600">{t("errorMessage")}</div>
          <button
            type="button"
            onClick={() => refetch()}
            className="btn-secondary mt-3"
          >
            {t("retry")}
          </button>
        </div>
      )}

      {!isLoading && !isError && dayGroups.length === 0 && (
        <div className="rounded-lg border border-secondary-200 bg-white p-10 text-center">
          <p className="text-sm font-medium text-secondary-900">{t("empty")}</p>
          <p className="mt-1 text-sm text-secondary-500">{t("emptyCta")}</p>
          <Link
            to="/sessions"
            className="mt-3 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {t("uploadFootage")} ›
          </Link>
        </div>
      )}

      {!isLoading && !isError && dayGroups.length > 0 && (
        <div
          className={`relative ${
            isPlaceholderData ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <div className="absolute bottom-2 left-[7px] top-2 w-px bg-secondary-200" />
          {dayGroups.map((group) => (
            <div key={group.day}>
              <div className="mb-3 ml-8 text-xs font-semibold uppercase tracking-widest text-secondary-500">
                {dayFormatter.format(new Date(`${group.day}T00:00:00.000Z`))}
              </div>
              {group.items.map((event) => (
                <FootageTimelineItem
                  key={event.id}
                  event={event}
                  timeFormatter={timeFormatter}
                />
              ))}
            </div>
          ))}
          {hasNextPage && (
            <div className="py-6 text-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="btn-secondary"
              >
                {t("loadMore")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
